import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

// the server doesn't push heartbeat updates, so poll while the page is visible
const POLL_INTERVAL_MS = 10000;

export default function useHeartbeatStatus() {
  const [heartbeats, setHeartbeats] = useState({
    display: {},
    esp: {},
    lastSeen: { display: {}, esp: {} },
    devices: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const { send, isConnected, registerHandler } = useWebSocketContext();

  const mountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);

  /**
   * Normalize ANY backend payload into:
   * { display: {}, esp: {}, lastSeen: { display: {}, esp: {} }, devices: {} }
   * lastSeen (unix seconds per gun) and devices (battery / temperature telemetry)
   * are newer additions; older servers don't send them.
   */
  const normalizeHeartbeatData = (payload) => {
    if (!payload || typeof payload !== "object") {
      return { display: {}, esp: {}, lastSeen: { display: {}, esp: {} }, devices: {} };
    }

    // Case 1: full backend response
    const src = payload.data && typeof payload.data === "object" ? payload.data : payload;
    return {
      display: src.display || {},
      esp: src.esp || {},
      lastSeen: {
        display: src.last_seen?.display || {},
        esp: src.last_seen?.esp || {},
      },
      devices: src.devices || {},
    };
  };

  const fetchHeartbeatStatus = useCallback(
    async (attempt = 0, { silent = false } = {}) => {
      if (!mountedRef.current || !isConnected) return;

      if (!silent) setIsLoading(true);
      setError("");

      try {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        const response = await send("cms_get_heartbeat_status", {});
        if (
          !response ||
          response.type !== "cms_get_heartbeat_status_response"
        ) {
          throw new Error("Invalid heartbeat response from server");
        }

        let payload = response.data;

        // 🔒 backend may send stringified JSON
        if (typeof payload === "string") {
          payload = JSON.parse(payload);
        }

        const normalized = normalizeHeartbeatData(payload);
        setHeartbeats(normalized);
        setUpdatedAt(new Date());
      } catch (err) {
        setError(err.message || "Failed to fetch heartbeat status");

        if (mountedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
          retryTimeoutRef.current = setTimeout(() => {
            fetchHeartbeatStatus(attempt + 1);
          }, delay);
        }
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, send]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchHeartbeatStatus();

    // 🔥 Live push updates
    const unregister = registerHandler(
      "heartbeat_status_update",
      (msg) => {
        if (!mountedRef.current) return;

        let payload = msg?.data;

        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch {
            return;
          }
        }

        const normalized = normalizeHeartbeatData(payload);
        setHeartbeats(normalized);
        setUpdatedAt(new Date());
      }
    );

    const poll = setInterval(() => {
      if (document.visibilityState === "visible" && !retryTimeoutRef.current) {
        fetchHeartbeatStatus(0, { silent: true });
      }
    }, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(poll);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      unregister();
    };
  }, [fetchHeartbeatStatus, registerHandler]);

  return {
    heartbeats,
    isLoading,
    error,
    updatedAt,
    refetch: () => fetchHeartbeatStatus(0),
  };
}
